// F12 · HeaderV4 — minimal premium-dark header (9 fixed links + user widget).
//
// Replaces HeaderV3 mega-menu. Order of links is RIGID:
//   Home · Pricing · Features · Modules · Marketplace · Ecosystems · Docs · Login · Register
//
// Logged-in state: Login + Register vanish, replaced by Dashboard CTA +
// avatar dropdown (email, role badge, Dashboard, Billing, Admin*, Logout).
//
// Rollback: `REACT_APP_HEADER_V4_ENABLED=false` keeps HeaderV3 mounted
// (handled by callers via `MainLayout`).

import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, Shield, CreditCard, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";
// Hotfix 2026-10 · marketplace cleanup · LanguageSwitcher + LocaleSwitcher
// rimossi dall'HeaderV4 pubblico; gli import restano scope-free (zero chiamate
// nel JSX) e saranno ripristinabili senza altre modifiche se richiesti.

// FASE I · `isSSR` flag drives full-page-reload via <a href> for routes
// served by Next.js SSR · SPA-internal routes stay <Link>.
// Hotfix 2026-10 · parità con SiteHeader SSR: aggiunta voce Suites.
const LINKS = [
    { to: "/",            label: "Home",        isSSR: true, testid: "nav-home" },
    { to: "/pricing",     label: "Pricing",     isSSR: true, testid: "nav-pricing" },
    { to: "/features",    label: "Features",    isSSR: true, testid: "nav-features" },
    { to: "/modules",     label: "Modules",     isSSR: true, testid: "nav-modules" },
    { to: "/marketplace/v10", label: "Marketplace", testid: "nav-marketplace" },  // SPA-interno (Fase G)
    { to: "/suites",      label: "Suites",      isSSR: true, testid: "nav-suites" },
    { to: "/ecosystems",  label: "Ecosystems",  isSSR: true, testid: "nav-ecosystems" },
    { to: "/docs",        label: "Docs",        isSSR: true, testid: "nav-docs" },
];

export default function HeaderV4() {
    const { user, logout } = useAuth();
    const { pathname } = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (!mobileOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [mobileOpen]);

    useEffect(() => {
        const onClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    // Close drawer and dropdown on route change
    useEffect(() => { setMobileOpen(false); setMenuOpen(false); }, [pathname]);

    const isAdmin = isAdminRole(user?.role);
    const isLogged = !!user;

    // Phase VI.5 Wave A · R2 Issue 3/4 · when an authenticated user
    // lands on a V12 Workspace alias (/v2/*), the public chrome
    // (HeaderV4) must step aside so the V12UserLayout can render its
    // achromatic shell without duplicate headers. Guests on /v2/* keep
    // the public chrome (ProtectedRoute would redirect them to / anyway).
    if (isLogged && pathname && (pathname.startsWith("/app/") || pathname.startsWith("/v2/"))) {
        return null;
    }

    const linkCls = ({ isActive }) =>
        [
            "text-sm font-medium transition-colors relative py-1.5",
            isActive
                ? "text-[#FFFFFF]"
                : "text-[#CCCCCC] hover:text-[#FFFFFF]",
        ].join(" ");

    return (
        <>
            <header
                data-testid="header-v4"
                className="sticky top-0 z-50 border-b"
                style={{
                    backgroundColor: "#000000",
                    borderColor: "#1A1A1A",
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
                    {/* Logo · nano-banana globe · hotfix 2026-10 */}
                    <a
                        href="/"
                        data-testid="header-v4-logo"
                        className="inline-flex items-center gap-2.5 shrink-0 no-underline
                                   transition-opacity hover:opacity-80"
                        aria-label="GlobalLeadSuite home"
                    >
                        <img
                            src="/logo-globe.png"
                            alt=""
                            width={32}
                            height={32}
                            className="h-8 w-8"
                        />
                        <span className="text-base font-semibold tracking-tight text-[#FFFFFF]">
                            GlobalLeadSuite
                        </span>
                    </a>

                    {/* Center nav — desktop only */}
                    <nav
                        data-testid="header-v4-nav-desktop"
                        className="hidden md:flex items-center gap-7 absolute left-1/2 -translate-x-1/2"
                    >
                        {LINKS.map((l) => (
                            l.isSSR ? (
                                <a
                                    key={l.to}
                                    href={l.to}
                                    className={linkCls({ isActive: false })}
                                    data-testid={l.testid || `header-v4-link-${l.label.toLowerCase()}`}
                                >
                                    <span>{l.label}</span>
                                </a>
                            ) : (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    className={linkCls}
                                    data-testid={l.testid || `header-v4-link-${l.label.toLowerCase()}`}
                                >
                                    {({ isActive }) => (
                                        <>
                                            <span>{l.label}</span>
                                            {isActive && (
                                                <span
                                                    className="absolute left-0 right-0 -bottom-1 h-[2px] rounded-full bg-[#CCCCCC]"
                                                    aria-hidden
                                                />
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            )
                        ))}
                    </nav>

                    {/* Right slot
                     * Hotfix 2026-10 · marketplace cleanup · the two
                     * language switchers (M14 compact + Wave-D locale)
                     * were removed from the SPA public chrome at the
                     * user's request. Locale resolution still happens
                     * server-side via cookie + Accept-Language.
                     */}
                    <div className="flex items-center gap-2">

                        {isLogged ? (
                            <>
                                <Link
                                    to="/dashboard"
                                    data-testid="header-v4-dashboard-btn"
                                    className="hidden md:inline-flex items-center gap-1.5 rounded-md bg-[#FFFFFF] hover:bg-[#EDEDED] text-black text-sm font-medium px-3.5 py-1.5 transition-colors no-underline"
                                >
                                    Dashboard
                                </Link>
                                <div className="relative" ref={menuRef}>
                                    <button
                                        type="button"
                                        onClick={() => setMenuOpen((o) => !o)}
                                        data-testid="header-v4-avatar-btn"
                                        className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] hover:border-white/20 px-1 py-1 transition-colors"
                                        aria-label="User menu"
                                        aria-expanded={menuOpen}
                                    >
                                        <span className="h-7 w-7 rounded-full bg-[#111111] grid place-items-center overflow-hidden">
                                            {user?.picture ? (
                                                <img src={user.picture} alt="" className="h-full w-full object-cover" />
                                            ) : (
                                                <span className="text-[11px] font-semibold text-[#FFFFFF]">
                                                    {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
                                                </span>
                                            )}
                                        </span>
                                        <ChevronDown className="h-3.5 w-3.5 text-[#CCCCCC]" />
                                    </button>
                                    <AnimatePresence>
                                        {menuOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -4 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -4 }}
                                                transition={{ duration: 0.15 }}
                                                data-testid="header-v4-user-dropdown"
                                                className="absolute right-0 mt-2 w-60 rounded-xl border border-white/10 bg-[#111111] shadow-2xl overflow-hidden"
                                            >
                                                <div className="px-4 py-3 border-b border-white/5">
                                                    <p className="text-sm font-medium text-[#FFFFFF] truncate">
                                                        {user?.name || user?.email}
                                                    </p>
                                                    <p className="text-xs text-[#CCCCCC] truncate">{user?.email}</p>
                                                    <span
                                                        data-testid="header-v4-role-badge"
                                                        className="mt-1.5 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-[#CCCCCC]/10 text-[#CCCCCC] border border-[#CCCCCC]/30"
                                                    >
                                                        {user?.role || "user"}
                                                    </span>
                                                </div>
                                                <DropdownItem to="/dashboard" testid="header-v4-menu-dashboard" icon={LayoutDashboard}>
                                                    Dashboard
                                                </DropdownItem>
                                                <DropdownItem to="/dashboard/billing" testid="header-v4-menu-billing" icon={CreditCard}>
                                                    Billing
                                                </DropdownItem>
                                                {isAdmin && (
                                                    <DropdownItem to="/admin/pipeline" testid="header-v4-menu-admin" icon={Shield}>
                                                        Admin
                                                    </DropdownItem>
                                                )}
                                                <div className="border-t border-white/5" />
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        setMenuOpen(false);
                                                        try { await logout(); } catch { /* noop */ }
                                                        window.location.href = "/";
                                                    }}
                                                    data-testid="header-v4-menu-logout"
                                                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#FAFAFA] hover:bg-[#FAFAFA]/10 transition-colors"
                                                >
                                                    <LogOut className="h-4 w-4" />
                                                    Logout
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    data-testid="header-v4-link-login"
                                    className="hidden md:inline-block text-sm font-medium text-[#CCCCCC] hover:text-[#FFFFFF] px-3 py-1.5 transition-colors no-underline"
                                >
                                    Login
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="header-v4-link-register"
                                    className="hidden md:inline-flex items-center rounded-md bg-[#FFFFFF] hover:bg-[#EDEDED] text-black text-sm font-medium px-3.5 py-1.5 transition-colors no-underline"
                                >
                                    Register
                                </Link>
                            </>
                        )}

                        {/* Mobile hamburger */}
                        <button
                            type="button"
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open menu"
                            data-testid="header-v4-hamburger"
                            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-[#FFFFFF] hover:bg-white/[0.06]"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer slide-right */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[60] md:hidden"
                        data-testid="header-v4-mobile-drawer"
                    >
                        <div
                            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                            onClick={() => setMobileOpen(false)}
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute right-0 top-0 bottom-0 w-full max-w-sm overflow-y-auto p-6"
                            style={{ backgroundColor: "#0A0A0A", borderLeft: "1px solid rgba(255,255,255,0.06)" }}
                        >
                            <div className="flex items-center justify-between mb-10">
                                <a
                                    href="/"
                                    onClick={() => setMobileOpen(false)}
                                    className="inline-flex items-center gap-2 no-underline"
                                >
                                    <img
                                        src="/logo-globe.png"
                                        alt=""
                                        width={28}
                                        height={28}
                                        className="h-7 w-7"
                                    />
                                    <span className="text-[15px] font-semibold text-[#FFFFFF]">GlobalLeadSuite</span>
                                </a>
                                <button
                                    type="button"
                                    onClick={() => setMobileOpen(false)}
                                    aria-label="Close menu"
                                    data-testid="header-v4-mobile-close"
                                    className="w-9 h-9 rounded-lg flex items-center justify-center text-[#FFFFFF] hover:bg-white/[0.06]"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <nav className="flex flex-col gap-1">
                                {LINKS.map((l) => (
                                    l.isSSR ? (
                                        <a
                                            key={l.to}
                                            href={l.to}
                                            onClick={() => setMobileOpen(false)}
                                            data-testid={l.testid ? `${l.testid}-mobile` : `header-v4-mobile-link-${l.label.toLowerCase()}`}
                                            className="block px-2 py-3 rounded-lg text-base text-[#CCCCCC] hover:text-[#FFFFFF] hover:bg-white/[0.03] no-underline"
                                        >
                                            {l.label}
                                        </a>
                                    ) : (
                                        <NavLink
                                            key={l.to}
                                            to={l.to}
                                            end={l.to === "/"}
                                            onClick={() => setMobileOpen(false)}
                                            data-testid={l.testid ? `${l.testid}-mobile` : `header-v4-mobile-link-${l.label.toLowerCase()}`}
                                            className={({ isActive }) =>
                                                `block px-2 py-3 rounded-lg text-base ${isActive ? "text-[#FFFFFF] bg-white/[0.04]" : "text-[#CCCCCC] hover:text-[#FFFFFF] hover:bg-white/[0.03]"} no-underline`
                                            }
                                        >
                                            {l.label}
                                        </NavLink>
                                    )
                                ))}
                            </nav>

                            <div className="mt-8 pt-6 border-t border-white/5">
                                {isLogged ? (
                                    <div className="flex flex-col gap-3">
                                        <Link
                                            to="/dashboard"
                                            onClick={() => setMobileOpen(false)}
                                            data-testid="header-v4-mobile-dashboard"
                                            className="block w-full text-center rounded-md bg-[#FFFFFF] hover:bg-[#EDEDED] text-black text-sm font-medium px-4 py-2.5 no-underline transition-colors"
                                        >
                                            Dashboard
                                        </Link>
                                        {isAdmin && (
                                            <Link
                                                to="/admin/pipeline"
                                                onClick={() => setMobileOpen(false)}
                                                data-testid="header-v4-mobile-admin"
                                                className="block w-full text-center rounded-md border border-white/10 text-sm font-medium px-4 py-2.5 text-[#FFFFFF] no-underline hover:bg-white/[0.03]"
                                            >
                                                Admin
                                            </Link>
                                        )}
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                setMobileOpen(false);
                                                try { await logout(); } catch { /* noop */ }
                                                window.location.href = "/";
                                            }}
                                            data-testid="header-v4-mobile-logout"
                                            className="block w-full text-center rounded-md border border-[#FAFAFA]/40 text-sm font-medium px-4 py-2.5 text-[#FAFAFA] hover:bg-[#FAFAFA]/10"
                                        >
                                            Logout
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        <Link
                                            to="/login"
                                            onClick={() => setMobileOpen(false)}
                                            data-testid="header-v4-mobile-link-login"
                                            className="block w-full text-center rounded-md border border-white/10 text-sm font-medium px-4 py-2.5 text-[#FFFFFF] no-underline hover:bg-white/[0.03]"
                                        >
                                            Login
                                        </Link>
                                        <Link
                                            to="/register"
                                            onClick={() => setMobileOpen(false)}
                                            data-testid="header-v4-mobile-link-register"
                                            className="block w-full text-center rounded-md bg-[#FFFFFF] hover:bg-[#EDEDED] text-black text-sm font-medium px-4 py-2.5 no-underline transition-colors"
                                        >
                                            Register
                                        </Link>
                                    </div>
                                )}
                                {/* Hotfix 2026-10 · language switchers
                                    rimossi dal mobile drawer coerentemente
                                    con il desktop · locale server-side. */}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function DropdownItem({ to, icon: Icon, children, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#FFFFFF] hover:bg-white/[0.04] transition-colors no-underline"
        >
            {Icon && <Icon className="h-4 w-4 text-[#CCCCCC]" />}
            {children}
        </Link>
    );
}
